import { STORES_CE_TYPES } from '../consts';
import { fetchPresetStructures } from './fetchDataUtils';
import { getWidgetToRootAppMap } from './headerWidgetsUtils';
import { WIX_GET_SUBSCRIBERS, WIX_STORES } from '@wix/app-definition-ids';

import type { PageSectionContent } from '@wix/editor-content-provider';
import type { SerializedCompStructure } from '@wix/document-services-types';
import {
  Dimensions,
  FooterPresetDefinition,
  FooterPresetStructure,
  HeaderPresetDefinition,
  HeaderPresetStructure,
} from '../types';
import { COMP_TYPES, logoAlignmentOptions } from '../siteGeneratorUtilsConsts';
import { transformCompStructure } from './siteGeneratorUtils';
import {
  LegalPageLinkData,
  PresetContentFieldRole,
} from '@wix/editor-content-injector';

export const getRelevantWidgetIdsFromChosenApps = (
  chosenApps: string[],
  isUsingSgOrigin: boolean,
): string[] => {
  const widgetToRootAppMap = getWidgetToRootAppMap(isUsingSgOrigin);
  return Object.entries(widgetToRootAppMap).reduce(
    (acc: string[], [widgetId, rootApps]) => {
      if (rootApps.some((rootApp) => chosenApps.includes(rootApp))) {
        acc.push(widgetId);
      }
      return acc;
    },
    [],
  );
};

export const filterHeaders = (
  headersList: HeaderPresetDefinition[],
  hasLogo: boolean,
): HeaderPresetDefinition[] => {
  return headersList.filter((header) => header.hasLogo === hasLogo);
};

export const adjustHeaderOrFooterWidgets = (
  isAppInstalled: (appDefId: string) => boolean,
  headerStructure: SerializedCompStructure,
): SerializedCompStructure => {
  const mutator = (comp: SerializedCompStructure) => {
    return filterHeaderOrFooterWidgets(isAppInstalled, comp);
  };
  return transformCompStructure(headerStructure, mutator);
};

export const filterHeaderOrFooterWidgets = (
  isAppInstalled: (appDefId: string) => boolean,
  comp: SerializedCompStructure,
): SerializedCompStructure => {
  if (!comp.components) {
    return comp;
  }
  comp.components = comp.components.filter((childComponent) =>
    filterWidget(isAppInstalled, childComponent),
  );
  return comp;
};

function filterWidget(
  isAppInstalled: (appDefId: string) => boolean,
  comp: SerializedCompStructure,
) {
  const id = comp.data?.appDefinitionId || comp.data?.applicationId;
  if (id) {
    return isAppInstalled(id);
  }
  return true;
}

export const adjustHeader = (
  headerStructure: SerializedCompStructure,
  isAppInstalled: (appDefId: string) => boolean,
): SerializedCompStructure => {
  return adjustHeaderOrFooterWidgets(isAppInstalled, headerStructure);
};

// Currently header needs only stores sourceTemplateId, so this is a specific handling for that case.
function getHeaderSourceTemplateId(
  appsToInstall: string[],
  sectionContents: PageSectionContent[],
): { sourceTemplateId?: string; appDefId?: string } {
  const storeSourceTemplateId = sectionContents.find(({ ceType }) =>
    STORES_CE_TYPES.includes(ceType),
  )?.sourceTemplateId;
  if (storeSourceTemplateId) {
    return {
      sourceTemplateId: storeSourceTemplateId,
      appDefId: WIX_STORES,
    };
  }
  return {};
}

export const getHeaderStructure = async (
  headerPreset: HeaderPresetDefinition | null,
  appsToInstall: string[],
  sectionContents: PageSectionContent[],
): Promise<HeaderPresetStructure | null> => {
  if (!headerPreset) return null;
  const {
    presetJsonUrl,
    presetMobileJsonUrl,
    title,
    _id,
    msid,
    logoAlignment,
    mobileLogoAlignment,
  } = headerPreset;
  const [structure, mobileStructure] = await fetchPresetStructures([
    presetJsonUrl,
    presetMobileJsonUrl,
  ]);
  const { sourceTemplateId, appDefId } = getHeaderSourceTemplateId(
    appsToInstall,
    sectionContents,
  );
  return {
    msid,
    title,
    id: _id,
    ceType: undefined,
    structure,
    mobileStructure,
    contentCategory: undefined,
    appDefId,
    sourceTemplateId,
    logoAlignment,
    mobileLogoAlignment,
  };
};

export const filterFooters = (
  footersList: FooterPresetDefinition[],
  hasLogo: boolean,
  appDefIds: string[],
): FooterPresetDefinition[] => {
  const isSubscribeAppInstalled = appDefIds.includes(WIX_GET_SUBSCRIBERS);
  return footersList.filter(
    (footer) =>
      footer.hasLogo === hasLogo &&
      footer.appIds.includes(WIX_GET_SUBSCRIBERS) === isSubscribeAppInstalled,
  );
};

export const adjustFooter = (
  footerStructure: SerializedCompStructure,
  socialLinks: Array<string>,
  legalPages?: LegalPageLinkData[],
  isAppInstalled?: (appDefId: string) => boolean,
): SerializedCompStructure => {
  let updatedFooterStructure = footerStructure;

  if (socialLinks.length === 0) {
    updatedFooterStructure = transformCompStructure(
      footerStructure,
      (compStructure: SerializedCompStructure) =>
        removeSocialLinksBar(compStructure),
    );
  }

  if (!legalPages || legalPages.length === 0) {
    updatedFooterStructure = transformCompStructure(
      updatedFooterStructure,
      (compStructure: SerializedCompStructure) =>
        removeLegalPagesComponent(compStructure),
    );
  }

  return isAppInstalled
    ? adjustHeaderOrFooterWidgets(isAppInstalled, updatedFooterStructure)
    : updatedFooterStructure;
};

export const getFooterStructure = async (
  footerPreset: FooterPresetDefinition | null,
  socialLinks: string[],
  legalPages?: LegalPageLinkData[],
): Promise<FooterPresetStructure | null> => {
  if (!footerPreset) return null;
  const {
    presetJsonUrl,
    presetMobileJsonUrl,
    title,
    _id,
    logoAlignment,
    msid,
    mobileLogoAlignment,
  } = footerPreset;
  const [footerDesktopStructure, mobileStructure] = await fetchPresetStructures(
    [presetJsonUrl, presetMobileJsonUrl],
  );
  const structure = adjustFooter(
    footerDesktopStructure,
    socialLinks,
    legalPages,
    (_appDefId: string) => true,
  );
  return {
    msid,
    title,
    id: _id,
    ceType: undefined,
    structure,
    mobileStructure,
    contentCategory: undefined,
    sourceTemplateId: undefined,
    logoAlignment,
    mobileLogoAlignment,
  };
};

const removeSocialLinksBar = (compStructure: SerializedCompStructure) => {
  if (
    compStructure.components &&
    compStructure.componentType !== COMP_TYPES.SOCIAL_LINK_BAR
  ) {
    compStructure.components = compStructure.components?.filter((childComp) => {
      return childComp.componentType !== COMP_TYPES.SOCIAL_LINK_BAR;
    });
  }
  return compStructure;
};

const removeLegalPagesComponent = (compStructure: SerializedCompStructure) => {
  if (
    compStructure.contentRole?.fieldRole !== PresetContentFieldRole.LegalPages
  ) {
    compStructure.components = compStructure.components?.filter((childComp) => {
      return (
        childComp.contentRole?.fieldRole !== PresetContentFieldRole.LegalPages
      );
    });
  }
  return compStructure;
};

export const collectLogoComponents = (
  compStructure: SerializedCompStructure,
  logoComponents: SerializedCompStructure[],
) => {
  if (compStructure.contentRole?.fieldRole === PresetContentFieldRole.Logo) {
    logoComponents.push(compStructure);
  }
  if (!compStructure.components) return;

  compStructure.components.forEach((childComp) => {
    collectLogoComponents(childComp, logoComponents);
  });
};

const adjustLogoComponentLayout = (
  logoCompStructure: SerializedCompStructure,
  logoAlignment: string,
  logoDimensions: Dimensions,
) => {
  if (!logoCompStructure.layout) return;

  const newWidth = Math.round(
    (logoCompStructure.layout.height / logoDimensions.height) *
      logoDimensions.width,
  );
  let layoutX;
  switch (logoAlignment) {
    case logoAlignmentOptions.Center:
      layoutX = Math.round(
        logoCompStructure.layout.x +
          (logoCompStructure.layout.width - newWidth) / 2,
      );
      break;
    case logoAlignmentOptions.Right:
      layoutX = Math.round(
        logoCompStructure.layout.x + logoCompStructure.layout.width - newWidth,
      );
      break;
    case logoAlignmentOptions.Left:
    default:
      layoutX = logoCompStructure.layout.x;
      break;
  }

  logoCompStructure.layout.width = newWidth;
  logoCompStructure.layout.x = layoutX;
};

export const adjustLogoLayout = (
  presetStructure: HeaderPresetStructure | FooterPresetStructure | null,
) => {
  if (!presetStructure) return;

  const desktopLogoComponents: SerializedCompStructure[] = [];
  const mobileLogoComponents: SerializedCompStructure[] = [];
  collectLogoComponents(presetStructure.structure, desktopLogoComponents);
  collectLogoComponents(presetStructure.mobileStructure, mobileLogoComponents);

  if (desktopLogoComponents.length === 0) return;

  const { width, height } = desktopLogoComponents[0].data;
  const logoDimensions: Dimensions = { width, height };

  desktopLogoComponents.forEach((logoCompStructure) => {
    adjustLogoComponentLayout(
      logoCompStructure,
      presetStructure.logoAlignment,
      logoDimensions,
    );
  });

  mobileLogoComponents.forEach((logoCompStructure) => {
    adjustLogoComponentLayout(
      logoCompStructure,
      presetStructure.mobileLogoAlignment,
      logoDimensions,
    );
  });
};

export const isFooter = (compStructure: SerializedCompStructure): boolean =>
  compStructure.componentType === COMP_TYPES.FOOTER;

export const isHeader = (compStructure: SerializedCompStructure): boolean =>
  compStructure.componentType === COMP_TYPES.HEADER;
